/**
 * Coverage Data Mapper
 * Transforms API responses into consistent internal format
 */

/**
 * Extract strength from drug description
 * Examples:
 * - "Atorvastatin Calcium 20 MG Oral Tablet" -> "20 MG"
 * - "Lisinopril 10 MG / Hydrochlorothiazide 12.5 MG Oral Tablet" -> "10 MG / 12.5 MG"
 * - "Insulin Glargine 100 UNT/ML Injectable Solution" -> "100 UNT/ML"
 * 
 * @param {string} description - Full drug description
 * @returns {string|null} Extracted strength or null
 */
function extractStrength(description) {
  if (!description) return null;

  // Match patterns like "20 MG", "10 MG / 12.5 MG", "100 UNT/ML", "0.5 %", etc.
  const strengthPattern = /(\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)*\s*(?:MG|MCG|G|ML|UNT|UNIT|MEQ|%|MG\/ML|MCG\/ML|UNT\/ML)(?:\s*\/\s*\d+(?:\.\d+)?\s*(?:MG|MCG|G|ML|UNT|UNIT|MEQ|%|MG\/ML|MCG\/ML|UNT\/ML))*)/i;
  
  const match = description.match(strengthPattern);
  return match ? match[1].trim() : null;
}

/**
 * Parse route and dose form from combined doseForm string
 * Examples:
 * - "Oral Tablet" -> { route: "Oral", doseForm: "Tablet" }
 * - "Intravenous Solution" -> { route: "Intravenous", doseForm: "Solution" }
 * - "Tablet" -> { route: null, doseForm: "Tablet" }
 * 
 * @param {string} doseFormString - Combined dose form string
 * @returns {Object} Object with route and doseForm properties
 */
function parseRouteAndDoseForm(doseFormString) {
  if (!doseFormString) return { route: null, doseForm: null };

  // Common routes to identify
  const routes = [
    'Oral',
    'Intravenous',
    'Intramuscular',
    'Subcutaneous',
    'Topical',
    'Ophthalmic',
    'Otic',
    'Nasal',
    'Rectal',
    'Vaginal',
    'Transdermal',
    'Inhalation',
    'Injectable',
    'Sublingual',
    'Buccal',
  ];

  // Check if any route is present at the beginning
  for (const route of routes) {
    const routeRegex = new RegExp(`^${route}\\s+(.+)`, 'i');
    const match = doseFormString.match(routeRegex);
    if (match) {
      return {
        route: route,
        doseForm: match[1].trim(),
      };
    }
  }

  // No route found, return entire string as doseForm
  return {
    route: null,
    doseForm: doseFormString.trim(),
  };
}

/**
 * Maps PrescriptSure API coverage response to internal CoverageData format
 * @param {Object} apiResponse - Raw API response
 * @param {Object} drugMetadata - Additional drug metadata from search
 * @returns {Object|null} Mapped coverage data or null if invalid
 */
export function mapCoverageResponse(apiResponse, drugMetadata = null) {
  try {
    // Check if we have valid response data
    if (
      !apiResponse?.result?.PlanCoverages ||
      apiResponse.result.PlanCoverages.length === 0
    ) {
      return null;
    }

    const planCoverage = apiResponse.result.PlanCoverages[0];

    // Determine coverage status from UnifiedTier
    const unifiedTierName = planCoverage.UnifiedTier?.name || '';
    const unifiedTierColor = planCoverage.UnifiedTier?.color || '';
    const drugListTierName = planCoverage.DrugListTier?.name || '';
    const formularyTierName = planCoverage.FormularyTier?.name || '';
    let status = 'no_data';

    // Check UnifiedTier first
    if (unifiedTierName.toLowerCase().includes('not covered')) {
      status = 'not_covered';
    } else if (unifiedTierName.toLowerCase().includes('covered')) {
      status = 'covered';
    } 
    // If UnifiedTier doesn't have clear status, check if there's a tier number
    else if (drugListTierName || formularyTierName) {
      // If there's a DrugListTier or FormularyTier, it means the drug is covered
      status = 'covered';
    }
    // Check if explicitly not covered
    else if (unifiedTierName.toLowerCase() === 'not covered' || 
             drugListTierName.toLowerCase() === 'not covered' ||
             formularyTierName.toLowerCase() === 'not covered') {
      status = 'not_covered';
    }

    // Extract drug information
    const drugDescription =
      drugMetadata?.drug_description || planCoverage.Product?.description || '';
    const doseFormString = drugMetadata?.drug_doseform;

    // Parse strength from description
    const strength = extractStrength(drugDescription);

    // Parse route and dose form
    const { route, doseForm } = parseRouteAndDoseForm(doseFormString);

    // Map to our data structure
    const coverageData = {
      drug: {
        name:
          drugMetadata?.drug_name ||
          planCoverage.Product?.name ||
          'Unknown Drug',
        description: drugDescription,
        type: planCoverage.Product?.is_generic === false ? 'Brand' : 'Generic',
        brandName: planCoverage.Product?.brand_name,
        // Parsed fields for DrugHeader
        strength: strength,
        route: route,
        doseForm: doseForm || doseFormString, // Fallback to original if parsing fails
        // Original field for backwards compatibility
        doseFormOriginal: doseFormString,
        therapeuticClass: drugMetadata?.therapeuticclass,
        therapeuticClassId: drugMetadata?.therapeuticclass_id,
        // FDB drug ID for drug info lookup
        drugMedId: drugMetadata?.drug_medid || null,
      },
      plan: {
        issuerName:
          planCoverage.Controller?.name ||
          planCoverage.Controller?.shortname ||
          'Unknown Issuer',
        planName: planCoverage.Plan?.name || 'Unknown Plan',
        planId: planCoverage.PlanId,
        planType: planCoverage.PlanType?.name || 'Unknown',
        channel: planCoverage.Channel?.name || 'Unknown',
      },
      coverage: {
        status,
        tier: extractTierNumber(planCoverage.DrugListTier?.name),
        tierDisplay:
          planCoverage.DrugListTier?.name ||
          planCoverage.FormularyTier?.name ||
          'Unknown Tier',
        drugListTier: planCoverage.DrugListTier?.name || '',
        formularyTier: planCoverage.FormularyTier?.name || '',
        unifiedTierName,
        unifiedTierColor,
        paRequired: planCoverage.PriorAuthorization === true,
        stRequired: planCoverage.StepTherapy === true,
        qlApplies: planCoverage.QuantityLimit === true,
        specialtyPharmacy: planCoverage.SpecialtyPharmacy === true,
        medicalBenefit: planCoverage.MedicalBenefit === true,
        conditionalPA: planCoverage.ConditionalPriorAuthorization === true,
      },
      notesAvailable: (planCoverage.NotesAvailable ?? 0) > 0,
      noteId: planCoverage.NoteId,
      monthId: planCoverage.MonthId,
      productId: planCoverage.ProductId,
      formularyId: planCoverage.FormularyId,
    };

    return coverageData;
  } catch (error) {
    console.error('Error mapping coverage response:', error);
    return null;
  }
}

/**
 * Extract tier number from tier name (e.g., "Tier 2" -> "2")
 * @param {string} tierName - Tier name string
 * @returns {string} Extracted tier number or "—"
 */
function extractTierNumber(tierName) {
  if (!tierName) return '—';

  const match = tierName.match(/\d+/);
  return match ? match[0] : '—';
}

/**
 * Maps medical coverage response to internal format
 * @param {Object} apiResponse - Raw API response
 * @returns {Object|null} Mapped medical coverage data or null if invalid
 */
export function mapMedicalCoverageResponse(apiResponse) {
  try {
    if (
      !apiResponse?.result?.PlanCoverages ||
      apiResponse.result.PlanCoverages.length === 0
    ) {
      return null;
    }

    const planCoverage = apiResponse.result.PlanCoverages[0];

    // Determine coverage status
    const isCovered = planCoverage.IsCovered;
    const status = isCovered ? 'covered' : 'not_covered';

    const medicalData = {
      drug: {
        name: planCoverage.Product.name,
        description: planCoverage.Product.description,
        type: planCoverage.Product.is_generic ? 'Generic' : 'Brand',
        brandName: planCoverage.Product.brand_name,
      },
      plan: {
        issuerName: planCoverage.Controller.name,
        planName: planCoverage.Plan.name,
        planId: planCoverage.PlanId,
        planType: planCoverage.PlanType.name,
        channel: planCoverage.Channel.name,
      },
      indication: {
        id: planCoverage.IndicationId,
        name: planCoverage.Indication.name,
      },
      coverage: {
        status,
        unifiedTierName: planCoverage.MedicalUnifiedTier.name,
        medicalUnifiedTierColor: planCoverage.MedicalUnifiedTier.color,
        paRequired: planCoverage.PA,
        stRequired: planCoverage.ST,
        medicalPolicy: planCoverage.MedicalPolicy,
        specialty: planCoverage.Specialty,
      },
      formulary: {
        name: planCoverage.Formulary.name,
        id: planCoverage.FormularyId,
      },
      noteIds: {
        paNoteId: planCoverage.PANoteId,
        stNoteId: planCoverage.STNoteId,
        specialtyPharmacyNoteId: planCoverage.SpecialtyPharmacyNoteId,
      },
      monthId: planCoverage.MonthId,
      productId: planCoverage.ProductId,
      lives: planCoverage.Lives,
    };

    return medicalData;
  } catch (error) {
    console.error('Error mapping medical coverage response:', error);
    return null;
  }
}

/**
 * Maps alternative coverage response to internal format
 * @param {Object} apiResponse - Raw API response
 * @returns {Array|null} Array of alternative drugs or null
 */
export function mapAlternativesResponse(apiResponse) {
  try {
    if (
      !apiResponse?.result?.PlanCoverages ||
      !apiResponse?.result?.Products
    ) {
      return null;
    }

    const { PlanCoverages, Products } = apiResponse.result;

    // Create a map of ProductId to Product details
    const productMap = {};
    Products.forEach((product) => {
      productMap[product.ProductId] = product;
    });

    // Map plan coverages with product details
    const alternatives = PlanCoverages.map((coverage) => {
      const product = productMap[coverage.ProductId] || {};
      const drug = product.Drug || {};

      return {
        productId: coverage.ProductId,
        name: drug.Name || coverage.Product?.name || 'Unknown',
        description: drug.Description || coverage.Product?.description || '',
        brand: drug.Brand,
        isGeneric: drug.IsGeneric ?? coverage.Product?.is_generic ?? true,
        doseForm: drug.DoseForm,
        strength: drug.Strength,
        therapeuticClass: product.TherapeuticClass,
        therapeuticClassId: product.TherapeuticClassId,
        tier: coverage.DrugListTier?.name || coverage.FormularyTier?.name,
        paRequired: coverage.PriorAuthorization,
        stRequired: coverage.StepTherapy,
        qlApplies: coverage.QuantityLimit,
      };
    });

    return alternatives;
  } catch (error) {
    console.error('Error mapping alternatives response:', error);
    return null;
  }
}

/**
 * Maps therapeutic alternatives response to internal format
 * @param {Object} apiResponse - Raw API response
 * @param {number} originalTherapeuticClassId - Original product's therapeutic class ID
 * @returns {Array|null} Array of therapeutic alternatives or null
 */
export function mapTherapeuticAlternativesResponse(
  apiResponse,
  originalTherapeuticClassId
) {
  try {
    if (!apiResponse?.result?.Products) {
      return null;
    }

    const { Products } = apiResponse.result;

    // Group by therapeutic class
    const groupedByClass = {};

    Products.forEach((product) => {
      const classId = product.TherapeuticClassId;
      const className = product.TherapeuticClass || 'Unknown Class';

      if (!groupedByClass[classId]) {
        groupedByClass[classId] = {
          therapeuticClassId: classId,
          therapeuticClass: className,
          isOriginalClass: classId === originalTherapeuticClassId,
          products: [],
        };
      }

      const drug = product.Drug || {};

      groupedByClass[classId].products.push({
        productId: product.ProductId,
        name: drug.Name || 'Unknown',
        description: drug.Description || '',
        brand: drug.Brand,
        isGeneric: drug.IsGeneric ?? true,
        doseForm: drug.DoseForm,
        strength: drug.Strength,
        rank: drug.Rank,
      });
    });

    // Convert to array and sort
    return Object.values(groupedByClass).sort((a, b) => {
      // Original class first
      if (a.isOriginalClass) return -1;
      if (b.isOriginalClass) return 1;
      // Then by class name
      return a.therapeuticClass.localeCompare(b.therapeuticClass);
    });
  } catch (error) {
    console.error('Error mapping therapeutic alternatives response:', error);
    return null;
  }
}
